import * as React from 'react';
import './DropdownMenu.scss';
import { noop } from '@deltasierra/utilities-object';
import { useClickOutside } from '@deltasierra/react-hooks';
import { BootstrapSize } from '../theme';

export type ButtonDisplayType = 'icon' | 'text';

export type DropdownMenuItemProps = {
    name: string;
    icon: string;
    onClick: () => void;
    selected?: boolean;
};

export const DropdownMenuItem: React.FunctionComponent<DropdownMenuItemProps> = ({ icon, name, onClick, selected }) => (
    <div className="dropdown-menu-item" onClick={onClick}>
        <span className={selected ? '' : 'text-primary'} style={{ color: selected ? '#c42a86' : undefined }}>
            <i className={icon} />
        </span>
        <span style={{ paddingLeft: 10 }}>{name}</span>
    </div>
);

DropdownMenuItem.displayName = 'DropdownMenuItem';

export type DropdownMenuBoxProps = {
    children: React.ReactNode;
    show: boolean;
    onShow?: () => void;
    onClose?: () => void;
};

export const DropdownMenuBox: React.FunctionComponent<DropdownMenuBoxProps> = ({
    children,
    onClose = noop,
    onShow = noop,
    show,
}) => {
    const [shown, setShown] = React.useState(false);
    const ref = useClickOutside<HTMLDivElement>(onClose, [onClose]);
    const handleClick = () => {
        onClose();
    };

    React.useEffect(() => {
        if (!shown && show) {
            onShow();
        }
        if (show) {
            setShown(true);
        } else {
            setShown(false);
        }
    }, [show, shown, onShow]);

    return (
        <>
            {shown && (
                <div className="dropdown-menu-box" ref={ref} onClick={handleClick}>
                    <div className="dropdown-menu-items">{children}</div>
                </div>
            )}
        </>
    );
};

DropdownMenuBox.displayName = 'DropdownMenuBox';

export type DropdownMenuProps = {
    children: React.ReactNode;
    buttonDisplayType: ButtonDisplayType;
    text?: string;
    icon?: string;
    buttonStyle?: string;
    buttonSize?: BootstrapSize;
};

export const DropdownMenu: React.FunctionComponent<DropdownMenuProps> = ({
    buttonDisplayType,
    buttonSize,
    buttonStyle = 'default',
    children,
    icon = '',
    text = '',
}) => {
    const [shown, setShown] = React.useState(false);
    const handleClick = () => {
        if (shown) {
            setShown(false);
        } else {
            setShown(true);
        }
    };
    const handleClose = () => {
        setShown(false);
    };
    const dropdownMenuBoxComponent = (
        <DropdownMenuBox show={shown} onClose={handleClose}>
            {children}
        </DropdownMenuBox>
    );

    switch (buttonDisplayType) {
        case 'icon': {
            return (
                <div className="dropdown">
                    <div className="dropdown-menu-button" onClick={handleClick}>
                        <span>
                            <i className={icon} />
                        </span>
                        <span className="caret"></span>
                    </div>
                    {dropdownMenuBoxComponent}
                </div>
            );
        }
        case 'text': {
            const size = buttonSize ? `btn-${buttonSize}` : '';
            return (
                <div className="dropdown">
                    <button
                        className={`btn btn-${buttonStyle} ${size} dropdown-toggle`}
                        type="button"
                        onClick={handleClick}
                    >
                        {text}
                        <span className="caret"></span>
                    </button>
                    {dropdownMenuBoxComponent}
                </div>
            );
        }
        default: {
            return <></>;
        }
    }
};

DropdownMenu.displayName = 'DropdownMenu';
